import {put, call} from 'redux-saga/effects';
import Task from "../../entities/task";
import * as Actions from "../actions/taskActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TaskAction = {
    taskId: string;
    task: Task;
    tasks: Task[];
};

export function* fetchOneTask(action: TaskAction) {
	try {
		const task: Task = yield call(api.findOne, 'task', action.taskId);

		// Now we can update the user interface
		yield put({type: Actions.TASK_FETCH_ONE_SUCCESS, taskId: action.taskId, task});
	}
	catch(err) {
		yield put({type: Actions.TASK_FETCH_ONE_FAILURE, taskId: action.taskId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TaskAction} action
 */
export function* saveTask(action: TaskAction) {
    // console.debug('⮂ TASK_SAVE saga', action.task?.id);
    
	if (action.task === undefined || action.task === null) {
		console.error('saveTask received a null');
		throw new Error('saveTask received a null');
	}

	if (!(action.task instanceof Task)) {
		action.task = new Task(action.task);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'task', action.task);
		if (addResult.isErr()) {
			console.error(`⮂ saveTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TASK_SAVE_FAILURE, task: action.task, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TASK_SAVE, task: action.task});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'task', action.task.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.TASK_SAVE_FAILURE, task: action.task, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {TaskAction} action
 * @param {Task[]} action.tasks
 */
export function* saveManyTasks(action: TaskAction) {
    // console.debug('⮂ TASK_SAVE_MANY saga', action.tasks.map(task => task.id).join(','));
    
    if (!Array.isArray(action.tasks)) {
        console.error('saveManyTasks expected an array, received', action.tasks);
        throw new Error('saveManyTasks expected an array');
    }

	try {
		const toSave: Task[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.tasks.length; i++) {
			const task = action.tasks[i];
			task.persisted = false;
			toSave.push(task);
		}

		const addResult = yield call(idb.add, 'task', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyTasks: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TASK_SAVE_MANY_FAILURE, tasks: action.tasks, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TASK_SAVE_MANY, tasks: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.TASK_SAVE_MANY_FAILURE, tasks: action.tasks, status: 500, err});

		console.error(`Error saving ${action.tasks.length} tasks to database`, err);
	}
}

/**
 * @param {TaskAction} action
 */
export function* removeTask(action: TaskAction) {
    const userId = getCurrentUserId();
	if (!userId || action.task === undefined || action.task === null || !(action.task instanceof Task)) {
			console.error('removeTask expected type Task, received', action.task);
			throw new Error('removeTask expected type Task');
		}

	try {
		// Mark as deleted
		action.task.status = 'D';
		action.task.addedOn = newHRTimestamp();
		action.task.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'task', action.task);
		if (addResult.isErr()) {
			console.error(`⮂ removeTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TASK_REMOVE_FAILURE, task: action.task, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TASK_REMOVE, task: action.task});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.TASK_REMOVE_FAILURE, task: action.task, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Task.
	*
	* @param {TaskAction} action
	*/
export function* restoreTask(action: TaskAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTask expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.task === undefined || action.task === null) {
        console.error('restoreTask expected a Task, received null');
        throw new Error('restoreTask expected a Task, received null');
    }
    else if (!(action.task instanceof Task)) action.task = new Task(action.task); 
    
	try {
		// Mark as restored
		action.task.status = 'U';
		action.task.addedOn = newHRTimestamp();
		action.task.addedBy = userId;
		action.task.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'task', action.task);
		if (addResult.isErr()) {
			console.error(`⮂ restoreTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TASK_RESTORE_FAILURE, task: action.task, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TASK_RESTORE, task: action.task});
	}
	catch(err) {
		yield put({type: Actions.TASK_RESTORE_FAILURE, task: action.task, status: 500, err});

		console.error('Error restoring from database', err);
	}
}